import { BinaryReader, BinaryWriter } from "cosmjs-types/binary.js";

import { CHAIN } from "./config.mjs";
import { websocketRpc } from "../probes/keplr-send/tx.mjs";

function fromBase64(value) {
  if (typeof Buffer !== "undefined") return Uint8Array.from(Buffer.from(value, "base64"));
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function hex(bytes) {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
}

async function abciQuery(path, requestBytes, rpcRequest = websocketRpc) {
  const result = await rpcRequest("abci_query", {
    path,
    data: hex(requestBytes),
    height: "0",
    prove: false,
  });
  const response = result?.response;
  if (Number(response?.code) !== 0) {
    throw new Error(response?.log || `ABCI query failed with code ${response?.code}`);
  }
  return fromBase64(response.value || "");
}

function embedded(bytes, callback) {
  const reader = new BinaryReader(bytes);
  while (reader.pos < reader.len) {
    const tag = reader.uint32();
    if ((tag >>> 3) === 1) {
      const length = reader.uint32();
      return callback(new BinaryReader(reader.buf.subarray(reader.pos, reader.pos + length)));
    }
    reader.skipType(tag & 7);
  }
  throw new Error("query response record is missing");
}

function decodeTerminalRecord(reader) {
  const value = {};
  while (reader.pos < reader.len) {
    const tag = reader.uint32();
    const field = tag >>> 3;
    if (field === 1) value.merchant_id = reader.string();
    else if (field === 2) value.terminal_id = reader.string();
    else if (field === 3) value.merchant_address = reader.string();
    else if (field === 4) value.active = reader.bool();
    else if (field === 5) value.terminal_key_id = reader.string();
    else if (field === 6) value.terminal_public_key = reader.string();
    else reader.skipType(tag & 7);
  }
  return value;
}

function decodeProductRecord(reader) {
  const value = {};
  while (reader.pos < reader.len) {
    const tag = reader.uint32();
    const field = tag >>> 3;
    if (field === 1) value.product_id = reader.string();
    else if (field === 2) value.tag_uid = reader.string();
    else if (field === 3) value.current_owner = reader.string();
    else if (field === 4) value.active = reader.bool();
    else if (field === 5) value.transferable = reader.bool();
    else if (field === 6) value.status = reader.string();
    else if (field === 7) value.metadata_commitment = reader.string();
    else if (field === 8) value.last_checkout_id = reader.string();
    else if (field === 9) value.transferred_at_ms = reader.int64().toString();
    else if (field === 10) value.transferred_at_height = reader.int64().toString();
    else reader.skipType(tag & 7);
  }
  return value;
}

function decodeCheckoutRecord(reader) {
  const names = new Map([
    [1, "checkout_id"], [2, "request_id"], [3, "response_id"], [4, "request_hash"],
    [5, "response_hash"], [6, "buyer"], [7, "merchant"], [8, "terminal_id"],
    [9, "merchant_id"], [10, "ipi_card_commitment"], [11, "denomination"],
    [12, "total_amount"], [14, "item_commitment"], [15, "nonce"],
    [16, "wallet_public_key_id"], [17, "status"], [21, "chain_id"],
    [22, "terminal_key_id"], [23, "card_verification_config_id"],
    [24, "card_proof_identity"],
  ]);
  const value = { product_ids: [] };
  while (reader.pos < reader.len) {
    const tag = reader.uint32();
    const field = tag >>> 3;
    if (names.has(field)) value[names.get(field)] = reader.string();
    else if (field === 13) value.product_ids.push(reader.string());
    else if (field === 18) value.settled_at_ms = reader.int64().toString();
    else if (field === 19) value.settled_at_height = reader.int64().toString();
    else if (field === 20) value.protocol_version = reader.uint32();
    else if (field === 25) value.card_read_counter = reader.uint64().toString();
    else reader.skipType(tag & 7);
  }
  return value;
}

async function restOrAbci({
  fetchFn,
  restPath,
  grpcPath,
  requestBytes,
  decode,
  rpcRequest,
}) {
  try {
    const response = await fetchFn(`${CHAIN.rest}${restPath}`);
    if (!response.ok) throw new Error(`chain query failed (HTTP ${response.status})`);
    const payload = await response.json();
    return payload.terminal ?? payload.product ?? payload.checkout;
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;
    return embedded(await abciQuery(grpcPath, requestBytes, rpcRequest), decode);
  }
}

export function encodeMerchantTerminalRequest(merchantId, terminalId) {
  return BinaryWriter.create().uint32(10).string(merchantId)
    .uint32(18).string(terminalId).finish();
}

export function encodeProductOwnerRequest(productId) {
  return BinaryWriter.create().uint32(10).string(productId).finish();
}

export function encodeCheckoutRequest(checkoutId) {
  return BinaryWriter.create().uint32(10).string(checkoutId).finish();
}

export async function queryMerchantTerminal(fetchFn, merchantId, terminalId, rpcRequest) {
  return restOrAbci({
    fetchFn,
    restPath: `/ipi/checkout/v1/merchants/${encodeURIComponent(merchantId)}`
      + `/terminals/${encodeURIComponent(terminalId)}`,
    grpcPath: "/ipi.checkout.v1.Query/MerchantTerminal",
    requestBytes: encodeMerchantTerminalRequest(merchantId, terminalId),
    decode: decodeTerminalRecord,
    rpcRequest,
  });
}

export async function queryProductOwner(fetchFn, productId, rpcRequest) {
  return restOrAbci({
    fetchFn,
    restPath: `/ipi/checkout/v1/products/${encodeURIComponent(productId)}/owner`,
    grpcPath: "/ipi.checkout.v1.Query/ProductOwner",
    requestBytes: encodeProductOwnerRequest(productId),
    decode: decodeProductRecord,
    rpcRequest,
  });
}

export async function queryCheckout(fetchFn, checkoutId, rpcRequest) {
  return restOrAbci({
    fetchFn,
    restPath: `/ipi/checkout/v1/checkouts/${encodeURIComponent(checkoutId)}`,
    grpcPath: "/ipi.checkout.v1.Query/Checkout",
    requestBytes: encodeCheckoutRequest(checkoutId),
    decode: decodeCheckoutRecord,
    rpcRequest,
  });
}
